import './ProjectDetailsTabs.scss';
import { TranslationKey, useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { useLocation } from 'react-router';
import { S_Tab, TabsSingleItemState } from '../../../../design/tabs/tab/S_Tab';
import { S_TabsContainer } from '../../../../design/tabs/tabsContainer/S_TabsContainer';
import { EnableFor } from '../../../containers/EnableForRole';
import { InputStatus } from '../../../models/error.model';
import { Home } from '../../../routes/config/Home';
import { ProjectStatus } from '../../projects/projects.model';
import { useProjectDetailsPermissions } from '../projectDetails.permissions';
import { ProjectDetailsImageListControlsContainer } from '../sub/images/containers/ProjectDetailsImageListControls.container';

interface IProps {
  projectId: string;
  projectStatus: ProjectStatus;
  workspaceId: string;
  imageClickCallback: () => Promise<void>;
  datasetsStatus: InputStatus;
  usersStatus: InputStatus;
  toolsStatus: InputStatus;
  activityLogStatus: InputStatus;
  imagesStatus: InputStatus;
}

export const ProjectDetailsTabs = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();
  const { pathname } = useLocation();
  const permissions = useProjectDetailsPermissions();

  const getClass = (link: string, status: InputStatus | undefined): TabsSingleItemState => {
    const pathname = window.location.pathname.split('/');
    const tabName = pathname[pathname.length - 1];
    let className = tabName === link ? 'active' : 'passive';
    if (status && status.isValid !== undefined) {
      className += status.isValid ? ' validated' : ' error';
    }
    return className as TabsSingleItemState;
  };

  const buildTab = (link: string, label: TranslationKey, publishedOnly: boolean, status: InputStatus | undefined, callback?: () => void): ReactElement => {
    if (publishedOnly && props.projectStatus === ProjectStatus.Draft) {
      return <></>;
    } else {
      return (
        <S_Tab
          text={t(label)}
          link={Home.Projects.Details.Paths.withParams({ projectId: props.projectId, tab: link, workspaceId: props.workspaceId })}
          state={getClass(link, status)}
          callback={callback}
        />
      );
    }
  };

  return (
    <div className='project-details-tabs'>
      <S_TabsContainer>
        {buildTab('overview', 'overview', true, undefined)}
        {buildTab('datasets', 'datasets', false, props.datasetsStatus)}
        {buildTab('images', 'images', false, props.imagesStatus, () => {
          void props.imageClickCallback();
        })}
        <EnableFor hasPermission={permissions.canViewWorkers()}>{buildTab('members', 'collaborators', false, props.usersStatus)}</EnableFor>
        {buildTab('annotation-view', 'annotation_tools', false, props.toolsStatus)}
        <EnableFor hasPermission={permissions.canExport()}>{buildTab('export-data', 'export_data', true, undefined)}</EnableFor>
        <EnableFor hasPermission={permissions.canViewStatistics()}>{buildTab('statistics', 'statistics', true, undefined)}</EnableFor>
        <EnableFor hasPermission={permissions.canViewActivityLog()}>{buildTab('activity-log', 'activity_log', false, props.activityLogStatus)}</EnableFor>
      </S_TabsContainer>

      {pathname.includes('images') && <ProjectDetailsImageListControlsContainer />}
    </div>
  );
});
